import { Injectable, signal, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { LocationAccessStatusEnum } from 'src/app/enums/location-access-status.enum';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { PusherService } from './pusher.service';

declare global {
  interface Window {
    process: any;
    electron: {
      ipcRenderer: {
        send: (...args: any[]) => void;
        invoke: (...args: any[]) => Promise<any>;
        on: (...args: any[]) => void;
        once: (...args: any[]) => void;
        removeListener: (...args: any[]) => void;
      };
      openExternalLink: (url: string | URL) => void;
      requestLocationAccess: () => Promise<LocationAccessRequestResult>;
      openLocationSettings: () => Promise<void>;
      onNavigate: (callback: (route: string) => void) => void;
      enableDoNotDisturb: () => void;
      disableDoNotDisturb: () => void;
      requestAccessibilityPermission: () => void;
      checkDoNotDisturbShortcuts: () => Promise<any>;
      setAngularUrl: (url: string) => void;
      getAngularUrl: () => Promise<string>;
      openNewWindow: () => void;
      goBack: () => Promise<void>;
      goForward: () => Promise<void>;
      canGoBack: () => Promise<boolean>;
      canGoForward: () => Promise<boolean>;
      getHistory: () => Promise<ElectronNavigationHistory>;
      goToIndex: (index: number) => Promise<void>;
      onNavigationUpdate: (callback: () => void) => () => void;
      onDisplayMediaPickerRequest: (callback: (data: ElectronDisplayMediaPickerRequest) => void) => void;
      selectDisplayMediaSource: (source_id: string, include_system_audio: boolean) => Promise<boolean>;
      cancelDisplayMediaPicker: () => Promise<boolean>;
      listDisplayMediaSources: () => Promise<DisplayMediaSourcesListResult>;
      requestMicrophoneAccess: () => Promise<MicrophoneAccessRequestResult>;
      requestScreenAccess: () => Promise<ScreenAccessRequestResult>;
      setRingoverMediaCaptureAllowed: (allowed: boolean) => Promise<{ allowed: boolean }>;
      copyToClipboard: (text: string) => void;
      copyImageToClipboard: (png_bytes: Uint8Array) => void;
    };
  }
}

export interface DisplayMediaSourceOption {
  id: string;
  name: string;
  thumbnail_data_url: string | null;
}

export interface DisplayMediaSourcesListResult {
  sources: DisplayMediaSourceOption[];
  supports_system_audio: boolean;
}

export interface MicrophoneAccessRequestResult {
  granted: boolean;
  status_before: 'not-determined' | 'granted' | 'denied' | 'restricted' | 'unknown';
  status_after: 'not-determined' | 'granted' | 'denied' | 'restricted' | 'unknown';
  prompted: boolean;
  can_prompt: boolean;
}

export interface ScreenAccessRequestResult {
  granted: boolean;
  status: 'not-determined' | 'granted' | 'denied' | 'restricted' | 'unknown';
}

export interface LocationAccessRequestResult {
  granted: boolean;
  status_before: LocationAccessStatusEnum;
  status_after: LocationAccessStatusEnum;
  prompted: boolean;
  can_prompt: boolean;
}

export interface ElectronNavigationHistory {
  entries: Array<{ title: string; url: string }>;
  active_index: number;
}

export interface ElectronDisplayMediaPickerRequest {
  audio_requested: boolean;
  video_requested: boolean;
  supports_system_audio: boolean;
  sources: {
    id: string;
    name: string;
    thumbnail_data_url: string | null;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  protected router = inject(Router);
  private injector = inject(Injector);

  private static readonly VERSION_CHECK_INTERVAL_MS = 60 * 60 * 1000;

  private ipcRenderer: any;
  public newVersionAvailable = signal<string | null>(null);
  private pusher: PusherService | null = null;
  private api: ApiService | null = null;
  private version_check_interval_id: ReturnType<typeof setInterval> | null = null;
  private display_media_picker_request_subject = new Subject<ElectronDisplayMediaPickerRequest>();
  readonly display_media_picker_request$: Observable<ElectronDisplayMediaPickerRequest> =
    this.display_media_picker_request_subject.asObservable();

  constructor() {
    if (this.isElectronApp()) {
      this.ipcRenderer = window.electron.ipcRenderer;
      this.listenForExternalNavigationEvents();
      this.listenForInternalClickOnExternalLinks();
      this.listenForDisplayMediaPickerRequests();
      this.sendAngularUrlToElectron();
    }
  }

  isElectronApp(): boolean {
    return !!(window && window.process && (window.process as any).type);
  }

  isMobile(): boolean {
    const hasTouchPoints = navigator.maxTouchPoints > 0;
    const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
    const hasNoHover = window.matchMedia?.('(hover: none)')?.matches ?? false;
    const minScreenSide = Math.min(window.innerWidth || 0, window.innerHeight || 0);
    const smallViewport = minScreenSide > 0 && minScreenSide <= 820;

    return (hasCoarsePointer && hasNoHover) || (hasTouchPoints && smallViewport);
  }

  isDesktop(): boolean {
    return !this.isMobile();
  }

  getBaseHref(): string {
    return '/';
  }

  enableDoNotDisturbIfAvailable() {
    if (this.isElectronApp()) {
      this.ipcRenderer.send('enable-do-not-disturb');
    }
  }

  disableDoNotDisturbIfAvailable() {
    if (this.isElectronApp()) {
      this.ipcRenderer.send('disable-do-not-disturb');
    }
  }

  requestAccessibilityPermissions() {
    // necessari per activar i desactivar do not disturb
    if (this.isElectronApp()) {
      this.ipcRenderer.send('request-accessibility-permission');
    }
  }

  openNewWindow() {
    if (this.isElectronApp()) {
      window.electron.openNewWindow();
    }
  }

  goBack(): void {
    if (this.isElectronApp()) {
      void window.electron.goBack();
    }
  }

  goForward(): void {
    if (this.isElectronApp()) {
      void window.electron.goForward();
    }
  }

  goToIndex(index: number): void {
    if (this.isElectronApp()) {
      void window.electron.goToIndex(index);
    }
  }

  async canGoBack(): Promise<boolean> {
    if (!this.isElectronApp()) {
      return false;
    }

    return window.electron.canGoBack();
  }

  async canGoForward(): Promise<boolean> {
    if (!this.isElectronApp()) {
      return false;
    }

    return window.electron.canGoForward();
  }

  async getHistory(): Promise<ElectronNavigationHistory> {
    if (!this.isElectronApp()) {
      return { entries: [], active_index: 0 };
    }

    return window.electron.getHistory();
  }

  onNavigationUpdate(callback: () => void): () => void {
    if (!this.isElectronApp()) {
      return () => undefined;
    }

    return window.electron.onNavigationUpdate(callback);
  }

  async openLocationSettings(): Promise<void> {
    if (!this.isElectronApp()) {
      return;
    }

    await window.electron.openLocationSettings();
  }

  async requestLocationAccess(): Promise<LocationAccessRequestResult> {
    if (!this.isElectronApp()) {
      return {
        granted: true,
        status_before: LocationAccessStatusEnum.NotApplicable,
        status_after: LocationAccessStatusEnum.NotApplicable,
        prompted: false,
        can_prompt: false,
      };
    }

    return window.electron.requestLocationAccess();
  }

  async selectDisplayMediaSource(source_id: string, include_system_audio: boolean): Promise<boolean> {
    if (!this.isElectronApp()) {
      return false;
    }

    return window.electron.selectDisplayMediaSource(source_id, include_system_audio);
  }

  async cancelDisplayMediaPicker(): Promise<boolean> {
    if (!this.isElectronApp()) {
      return false;
    }

    return window.electron.cancelDisplayMediaPicker();
  }

  async listDisplayMediaSources(): Promise<DisplayMediaSourcesListResult> {
    if (!this.isElectronApp()) {
      return {
        sources: [],
        supports_system_audio: false,
      };
    }

    return window.electron.listDisplayMediaSources();
  }

  async requestMicrophoneAccess(): Promise<MicrophoneAccessRequestResult> {
    if (!this.isElectronApp()) {
      return {
        granted: true,
        status_before: 'granted',
        status_after: 'granted',
        prompted: false,
        can_prompt: false,
      };
    }

    return window.electron.requestMicrophoneAccess();
  }

  async requestScreenAccess(): Promise<ScreenAccessRequestResult> {
    if (!this.isElectronApp()) {
      return { granted: true, status: 'granted' };
    }

    return window.electron.requestScreenAccess();
  }

  /**
   * Electron-only: allow/deny getUserMedia for the Ringover iframe.
   * Denied while idle so macOS keeps Bluetooth headphones in music (A2DP) mode.
   */
  async setRingoverMediaCaptureAllowed(allowed: boolean): Promise<void> {
    if (!this.isElectronApp()) {
      return;
    }

    await window.electron.setRingoverMediaCaptureAllowed(allowed);
  }

  async restartAndInstallUpdate(): Promise<{ success: boolean; reason?: string }> {
    if (!this.isElectronApp()) {
      return { success: false, reason: 'not_electron' };
    }

    return this.ipcRenderer.invoke('restart-and-install-update', this.router.url);
  }

  clearNewVersionAvailable() {
    this.newVersionAvailable.set(null);
  }

  private listenForExternalNavigationEvents() {
    if (this.isElectronApp()) {
      window.electron.onNavigate((route: string) => {
        const normalized = route.startsWith('/') ? route : `/${route}`;
        const is_oauth_callback = normalized.startsWith('/auth/oauth/callback');

        if (is_oauth_callback || confirm('¿Estás seguro que quieres navegar a la página ' + normalized + '?')) {
          void this.router.navigateByUrl(normalized);
        }
      });
    }
  }

  private listenForInternalClickOnExternalLinks() {
    if (this.isElectronApp()) {
      // 1. Escoltem links
      document.addEventListener('click', event => {
        const target = event.target as HTMLAnchorElement;
        const anchorElement = target.closest('a');

        if (anchorElement && anchorElement.href) {
          const url: string = anchorElement.href;
          this.handleLinkClick(event, url);
        }
      });

      // 2. Escoltem window.open() de <button>
      const originalWindowOpen = window.open;
      window.open = (url: string | URL | undefined, target?: string | undefined, features?: string | undefined): Window | null => {
        if (url !== undefined && this.isLinkExternalInElectron(typeof url === 'string' ? url : url.toString())) {
          window.electron.openExternalLink(url);
        } else {
          // Si es un enlace interno, usar la función original
          return originalWindowOpen(url, target, features);
        }
        return null;
      };
    }
  }

  private handleLinkClick(event: MouseEvent, url: string) {
    if (this.isLinkExternalInElectron(url)) {
      event.preventDefault();
      window.electron.openExternalLink(url);
    }
  }

  private isLinkExternalInElectron(url: string) {
    if (url.startsWith(`${environment.electron_schema}://`) || url.startsWith('bisualintranet://')) {
      return false;
    }

    return url.startsWith('http');
  }

  private sendAngularUrlToElectron() {
    if (this.isElectronApp()) {
      window.electron.setAngularUrl(environment.angular_url);
    }
  }

  private listenForDisplayMediaPickerRequests() {
    if (!this.isElectronApp()) {
      return;
    }

    window.electron.onDisplayMediaPickerRequest((request: ElectronDisplayMediaPickerRequest) => {
      this.display_media_picker_request_subject.next(request);
    });
  }

  subscribeToVersionUpdates() {
    if (!this.isElectronApp()) {
      return;
    }

    // Obtener PusherService de forma lazy para evitar dependencias circulares
    if (!this.pusher) {
      this.pusher = this.injector.get(PusherService);
    }

    this.pusher.subscribeToFrontendVersionUpdatesChannel((data: { version: string; is_production: boolean }) => {
      this.applyVersionUpdateIfRelevant(data.version, data.is_production);
    });

    this.checkLatestFrontendVersionFromApi();
    this.startFrontendVersionPolling();
  }

  private startFrontendVersionPolling(): void {
    if (this.version_check_interval_id !== null) {
      return;
    }

    this.version_check_interval_id = setInterval(() => {
      this.checkLatestFrontendVersionFromApi();
    }, PlatformService.VERSION_CHECK_INTERVAL_MS);
  }

  private checkLatestFrontendVersionFromApi(): void {
    const requested_environment = this.resolveFrontendVersionEnvironment();
    if (requested_environment === null) {
      return;
    }

    if (!this.api) {
      this.api = this.injector.get(ApiService);
    }

    this.api.getLatestFrontendVersion(requested_environment).subscribe({
      next: data => {
        if (data.version === null) {
          return;
        }

        this.applyVersionUpdateIfRelevant(data.version, data.is_production);
      },
      error: () => {
        // Ignore polling errors; Pusher remains the primary channel.
      },
    });
  }

  private resolveFrontendVersionEnvironment(): 'production' | 'staging' | null {
    if (environment.environment === 'production') {
      return 'production';
    }

    if (environment.environment === 'staging') {
      return 'staging';
    }

    return null;
  }

  private applyVersionUpdateIfRelevant(version: string, is_production: boolean): void {
    const is_event_for_this_environment = is_production === (environment.environment === 'production');
    if (!is_event_for_this_environment) {
      return;
    }

    if (this.normalizeVersion(version) !== this.normalizeVersion(environment.version)) {
      this.newVersionAvailable.set(version);
    }
  }

  private normalizeVersion(version: string): string {
    return version.replace(/^v/i, '');
  }
}

/** Paths workers may use on desktop web when Electron force is enabled. */
export const DESKTOP_WEB_ALLOWED_PATH_PREFIXES = [
  '/employees/personal',
  '/employees/performance/payrolls',
  '/employees/activity/calendar',
  '/employees/my-profile',
  '/employees/use-desktop-app',
] as const;

export function normalizeRouterPath(url: string): string {
  const withoutQuery = url.split('?')[0] ?? url;
  const withoutHash = withoutQuery.split('#')[0] ?? withoutQuery;
  if (!withoutHash) {
    return '/';
  }

  return withoutHash.startsWith('/') ? withoutHash : `/${withoutHash}`;
}

export function isDesktopWebAllowedUrl(url: string): boolean {
  const path = normalizeRouterPath(url);

  return DESKTOP_WEB_ALLOWED_PATH_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
}

export function shouldForceDesktopApp(
  user: { role?: { slug?: string } | null } | null | undefined,
  force_employees_to_use_desktop_app: boolean,
  is_electron_app: boolean,
  is_desktop: boolean,
  environment_name: string
): boolean {
  return (
    user?.role?.slug === 'worker' &&
    !is_electron_app &&
    is_desktop &&
    environment_name === 'production' &&
    force_employees_to_use_desktop_app
  );
}
